import { ButtonGradientPrimary, Card, Colors, Icon } from '@amelio/ui-library';
import { useEffect, useState } from 'react';

import { DeleteMeetingTopicDialog } from './DeleteMeetingTopicDialog';
import { MeetingTopicModal } from './MeetingTopicModal';

import { useSaveMeetingTopics } from '@/api/cycle-evaluation-services/manager-assessment';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { PlanLockIcon, usePlanLock } from '@/components/ProGate/PlanLock';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';

interface MeetingTopic {
    // A real persisted id, or a temporary `new-*` id for a topic not yet saved.
    id: string;
    subject: string;
    notes: string;
}

interface MeetingTopicsProps {
    employeeFirstName: string;
    cycleInstanceId: string;
    initialTopics?: MeetingTopic[];
    dataTestId?: string;
    disabled?: boolean;
}

const isNewId = (id: string): boolean => id.startsWith('new-');

// Stable reference for the omitted-prop case: a fresh `[]` default would change identity every
// render, refiring the sync effect below and causing an infinite re-render loop.
const EMPTY_MEETING_TOPICS: MeetingTopic[] = [];

export const MeetingTopics = ({
    employeeFirstName,
    cycleInstanceId,
    initialTopics = EMPTY_MEETING_TOPICS,
    dataTestId = 'meeting-topics',
    disabled = false,
}: MeetingTopicsProps) => {
    const { t } = useAmelioTranslations();
    const planLock = usePlanLock();
    const { handleError } = useError();
    const { mutateAsync: saveMeetingTopics, isPending: isSaving } = useSaveMeetingTopics();

    const [topics, setTopics] = useState<MeetingTopic[]>(initialTopics);
    // The CycleInstance loads async, so sync in the persisted topics when they arrive (and after
    // a save refetch). `initialTopics` is memoized by the page, so this only fires on real changes.
    useEffect(() => {
        setTopics(initialTopics);
    }, [initialTopics]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [deletingTopic, setDeletingTopic] = useState<MeetingTopic | null>(null);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    // Persist the full set of topics (the endpoint replaces them) and re-sync local ids with the
    // persisted ones returned by the server.
    const persist = async (next: MeetingTopic[]) => {
        setTopics(next);
        try {
            const updated = await saveMeetingTopics({
                cycleInstanceId,
                payload: {
                    topics: next.map((topic, index) => ({
                        id: isNewId(topic.id) ? undefined : topic.id,
                        subject: topic.subject,
                        notes: topic.notes || null,
                        sortOrder: index,
                    })),
                },
            });

            setTopics(
                [...(updated.meetingTopics ?? [])]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((topic) => ({
                        id: topic.id,
                        subject: topic.subject,
                        notes: topic.notes ?? '',
                    })),
            );
        } catch (error) {
            handleError({ error });
        }
    };

    const editingTopic = editingId ? topics.find((topic) => topic.id === editingId) : undefined;

    const handleOpenAdd = () => {
        if (planLock.isLocked) {
            planLock.onLocked();
            return;
        }
        if (disabled) return;
        setEditingId(null);
        setIsModalOpen(true);
    };

    const handleOpenEdit = (id: string) => {
        if (disabled) return;
        setEditingId(id);
        setIsModalOpen(true);
    };

    const handleModalSave = ({ subject, notes }: { subject: string; notes: string }) => {
        const next = editingId
            ? topics.map((topic) => (topic.id === editingId ? { ...topic, subject, notes } : topic))
            : [...topics, { id: `new-${Date.now()}`, subject, notes }];

        setIsModalOpen(false);
        setEditingId(null);
        void persist(next);
    };

    const deleteTopic = (topicId: string) => {
        setDeletingTopic(null);
        void persist(topics.filter((topic) => topic.id !== topicId));
    };

    // Always confirmed, like a topic of the Meet step's agenda: the topic is saved as soon as it
    // goes, so a stray click must not be enough to lose it.
    const handleDeleteTopic = (topic: MeetingTopic) => {
        setDeletingTopic(topic);
    };

    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (event: React.DragEvent, index: number) => {
        event.preventDefault();
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (dropIndex: number) => {
        if (disabled) {
            return;
        }
        if (draggedIndex !== null && draggedIndex !== dropIndex) {
            const reordered = [...topics];
            const [dragged] = reordered.splice(draggedIndex, 1);
            reordered.splice(dropIndex, 0, dragged);
            void persist(reordered);
        }
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const getDragItemClassName = (index: number): string => {
        if (draggedIndex === index) {
            return 'opacity-50';
        }
        if (dragOverIndex === index) {
            return 'scale-105 rounded-lg border-2 border-dashed border-teal-500 bg-teal-500/10 px-2';
        }
        return '';
    };

    return (
        <Card
            padding={'none'}
            data-testid={dataTestId}
            className={'!mt-[85px] rounded-[12px]! p-[16px] md:p-[26px]!'}
        >
            <div className={'flex items-center justify-between'}>
                <h2 className={'text-[20px] font-medium text-gray-900'}>
                    {t('managerAssessmentEvaluation.meetingTopics.title', {
                        name: employeeFirstName,
                    })}
                </h2>
            </div>

            <div className={'mt-3 flex flex-col gap-4 border-t border-slate-400/40 pt-4'}>
                <p className={'mb-3 text-[14px] leading-relaxed text-gray-500'}>
                    {t('managerAssessmentEvaluation.meetingTopics.description')}
                </p>

                <div className={'flex flex-col gap-6 rounded-lg bg-gray-50/50 p-6'}>
                    {topics.length === 0 && (
                        <EmptyState
                            message={t('managerAssessmentEvaluation.meetingTopics.noTopics')}
                        />
                    )}

                    {topics.map((topic, index) => (
                        <div
                            key={topic.id}
                            draggable={!disabled}
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(event) => handleDragOver(event, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={() => handleDrop(index)}
                            className={`flex cursor-move gap-2 transition-all duration-200 ${getDragItemClassName(index)}`}
                            data-testid={`meeting-topic-${index}`}
                        >
                            <div
                                className={
                                    'flex h-6 w-6 shrink-0 items-center justify-center pt-1 text-teal-500'
                                }
                            >
                                <Icon
                                    name={'essential-menu'}
                                    size={20}
                                    color={Colors.SECONDARY}
                                />
                            </div>

                            <div className={'flex flex-1 flex-col gap-3'}>
                                <div
                                    className={
                                        'flex items-center justify-between rounded-lg border border-gray-200 bg-white px-5 py-4 transition-colors hover:border-gray-300 hover:bg-white/80'
                                    }
                                >
                                    <button
                                        type={'button'}
                                        onClick={() => handleOpenEdit(topic.id)}
                                        disabled={disabled}
                                        className={
                                            'flex flex-1 flex-col items-start gap-1 text-left'
                                        }
                                        data-testid={`topic-edit-${index}`}
                                    >
                                        <h4
                                            className={
                                                'text-body font-semibold tracking-[0.16px] text-gray-900'
                                            }
                                        >
                                            {topic.subject}
                                        </h4>

                                        {topic.notes && (
                                            <div
                                                className={
                                                    'flex items-center gap-1 text-[11px] font-medium text-teal-500'
                                                }
                                            >
                                                <Icon
                                                    name={'essential-tick-circle'}
                                                    size={12}
                                                    color={Colors.SECONDARY}
                                                />

                                                <span>
                                                    {t(
                                                        'managerAssessmentEvaluation.meetingTopics.detailsAdded',
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </button>

                                    {(!disabled || planLock.isLocked) && (
                                        <button
                                            type={'button'}
                                            onClick={() =>
                                                planLock.isLocked
                                                    ? planLock.onLocked()
                                                    : handleDeleteTopic(topic)
                                            }
                                            className={
                                                'shrink-0 p-1.5 text-gray-400 transition-colors hover:text-red-500'
                                            }
                                            data-testid={`topic-delete-${index}`}
                                        >
                                            {planLock.isLocked ? (
                                                <PlanLockIcon color={Colors.BASE_300} />
                                            ) : (
                                                <Icon
                                                    name={'essential-trash'}
                                                    size={16}
                                                    color={Colors.BASE_300}
                                                />
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <ButtonGradientPrimary
                    type={'button'}
                    onClick={handleOpenAdd}
                    disabled={disabled && !planLock.isLocked}
                    className={
                        'flex h-auto! !min-w-[178px] scale-100! items-center gap-2 self-start rounded-[8px]! rounded-lg px-4! py-[8px]! !text-sm font-medium text-white shadow-sm transition-all hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50'
                    }
                    data-testid={'add-meeting-topic-button'}
                >
                    {planLock.isLocked ? (
                        <PlanLockIcon
                            color={Colors.WHITE}
                            className={'mr-2'}
                        />
                    ) : (
                        <Icon
                            name={'essential-add'}
                            size={16}
                            color={Colors.WHITE}
                            className={'mr-2'}
                        />
                    )}

                    {t('managerAssessmentEvaluation.meetingTopics.addPoint')}
                </ButtonGradientPrimary>
            </div>

            <MeetingTopicModal
                isOpen={isModalOpen}
                initialSubject={editingTopic?.subject ?? ''}
                initialNotes={editingTopic?.notes ?? ''}
                isSaving={isSaving}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingId(null);
                }}
                onSave={handleModalSave}
            />

            <DeleteMeetingTopicDialog
                isOpen={deletingTopic !== null}
                subject={deletingTopic?.subject ?? ''}
                hasNotes={Boolean(deletingTopic?.notes)}
                onConfirm={() => deletingTopic && deleteTopic(deletingTopic.id)}
                onCancel={() => setDeletingTopic(null)}
                dataTestId={`${dataTestId}-delete-dialog`}
            />
        </Card>
    );
};
